import { Center, Tag, Text, VStack, Tooltip } from '@chakra-ui/react';
import { DataTable } from '@gamma/ui/data-table';
import { FlushPanelContent, Panel } from '@gamma/ui/layout';

import { LoadingSpinner } from '@gamma/ui/progress';
import moment from 'moment';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Column as TableColumn } from 'react-table';
import {
  useDateRangeQueryString,
  useManualPagination,
} from '@investigator/shared/hooks';
import { useEntitiesContext, useEntityEdrContext } from '../../../context';
import {
  NICHistory,
  useGetNicHistoryForEndpointDeviceEntityQuery,
} from '../../../queries';
import {
  UserHistory,
  EntityDataStoreEntityType,
} from '@investigator/shared/generated/graphql';
import { IdentityAlertsPopover } from './IdentityAlertsPopover';
import { useIdentityIntegrationSettings } from '@investigator/features/integrations/hooks';
import { MISSING_DATA_PLACEHOLDER } from '@investigator/shared/constants';
import { i18n } from '@investigator/shared/localization';
import { NoDataSection } from '../NoDataSection';

export const CrowdstikeUserHistoryDataSection = (): JSX.Element => {
  const { selectedEntity } = useEntitiesContext();
  const { userHistoryTableHeaders } = i18n.pages.detections.entityEDR;
  const { identityIntegrationEnabled } = useIdentityIntegrationSettings();
  const { edrData, crowdstrikeUserHistory } = useEntityEdrContext();
  const [nicInitialFetch, nicSetInitialFetch] = useState<boolean>(false);
  const { start, end } = useDateRangeQueryString();
  const userTableRef = useRef<HTMLDivElement>(null);
  const nicTableRef = useRef<HTMLDivElement>(null);
  // Get crowdstrike data from context - parent components ensure this exists
  const crowdstrikeData = edrData?.crowdstrike;
  const { tenantInfo, lastSeen } = selectedEntity || {};
  const tenant_id = tenantInfo?.tenant_id ?? '';

  const entity_id = crowdstrikeData?.entity_id ?? '';
  // Get user history from context
  const {
    data: userHistoryItems,
    totalItems: userTotalItems,
    loading: getUserHistoryLoading,
    error: _getUserHistoryError,
    fetchHistory,
  } = crowdstrikeUserHistory;

  const dataTableUserColumns: TableColumn<UserHistory>[] = useMemo(
    () => [
      {
        Header: userHistoryTableHeaders.lastLogin,
        accessor: (row) => row.obsts || '-',
        Cell: ({ value }: { value: number }) => {
          return moment.unix(value).format('MMM D, YYYY');
        },
      },
      {
        Header: userHistoryTableHeaders.loginId,
        accessor: (row) =>
          row.identity_user?.username ||
          row.username ||
          MISSING_DATA_PLACEHOLDER,
        id: 'login_id',
        disableSortBy: true,
      },
      ...(identityIntegrationEnabled
        ? [
            {
              Header: userHistoryTableHeaders.displayName,
              accessor: (row: UserHistory) =>
                row?.identity_user?.display_name || MISSING_DATA_PLACEHOLDER,
              id: 'display_name',
              disableSortBy: true,
            },
            {
              Header: userHistoryTableHeaders.riskScore,
              accessor: (row: UserHistory) =>
                row?.identity_user?.risk_score || 0,
              id: 'risk_score',
              Cell: ({ value }: { value: number }) => {
                const colorScheme =
                  value >= 60 ? 'orange' : value >= 40 ? 'yellow' : 'green';
                return (
                  <Tag
                    colorScheme={colorScheme}
                    size="md"
                    variant="subtle"
                    borderRadius="100px"
                    borderWidth="1px"
                    borderColor="whiteAlpha.200"
                  >
                    {value}
                  </Tag>
                );
              },
              disableSortBy: true,
            },
            {
              Header: userHistoryTableHeaders.userEmail,
              accessor: (row: UserHistory) =>
                row.identity_user?.email || MISSING_DATA_PLACEHOLDER,
              id: 'user_email',
              Cell: ({ value }: { value: string }) => (
                <Tooltip label={value} placement="top" hasArrow>
                  <Text
                    noOfLines={1}
                    fontSize="md"
                    color="text.primary"
                    maxW="220px"
                  >
                    {value || MISSING_DATA_PLACEHOLDER}
                  </Text>
                </Tooltip>
              ),
              disableSortBy: true,
            },
            {
              Header: userHistoryTableHeaders.identityAlerts,
              accessor: (row: UserHistory) => row.identity_user,
              id: 'identity_alerts',
              Cell: ({
                value: identityUser,
              }: {
                value: UserHistory['identity_user'];
              }) => {
                const count = identityUser?.identity_alerts_count || 0;
                const userId = identityUser?.user_id || '';
                if (count === 0) return MISSING_DATA_PLACEHOLDER;
                return (
                  <IdentityAlertsPopover alertCount={count} userId={userId} />
                );
              },
              disableSortBy: true,
            },
          ]
        : []),
    ],
    [identityIntegrationEnabled],
  );

  const {
    sortBy: nicSortBy,
    offset: nicOffset,
    pageSize: nicPageSize,
    pageIndex: nicPageIndex,
    getPageCount: nicGetPageCount,
    getSortsForAPI: nicGetSortsForAPI,
  } = useManualPagination<NICHistory>({
    initialFetch: nicInitialFetch,
    sortBy: [{ id: 'obsts', desc: true }],
  });

  const { error: _getNicHistoryError } =
    useGetNicHistoryForEndpointDeviceEntityQuery({
      skip: !lastSeen || !entity_id,
      variables: {
        as_of: lastSeen,
        entity: {
          entity_id,
          entity_type: EntityDataStoreEntityType.EndpointDevice,
        },
        tenant: tenant_id,
        offset: nicOffset,
        size: nicPageSize,
        sort: nicGetSortsForAPI(nicSortBy),
        use_cache: true,
      },
      fetchPolicy: 'cache-and-network',
      onCompleted: ({ getNicHistoryForEndpointDeviceEntity }) => {
        nicSetInitialFetch(false);
        nicGetPageCount(getNicHistoryForEndpointDeviceEntity);
      },
    });

  const {
    offset: userOffset,
    pageSize: userPageSize,
    pageIndex: userPageIndex,
    pageCount: userPageCount,
    onFetchData: userOnFetchData,
    getPageCount: userGetPageCount,
  } = useManualPagination<UserHistory>({
    initialFetch: true,
  });

  // Sync pagination with context
  React.useEffect(() => {
    fetchHistory(userOffset, userPageSize);
  }, [userOffset, userPageSize, fetchHistory]);

  React.useEffect(() => {
    if (!getUserHistoryLoading || userTotalItems > 0) {
      userGetPageCount({ total_items: userTotalItems, size: userPageSize });
    }
  }, [userTotalItems, userPageSize, userGetPageCount, getUserHistoryLoading]);

  useEffect(() => {
    nicSetInitialFetch(true);
  }, [start, end]);

  // Scroll User History table back into view when page changes
  useEffect(() => {
    if (userPageIndex > 0 && userTableRef.current) {
      userTableRef.current.scrollIntoView({ behavior: 'auto', block: 'start' });
    }
  }, [userPageIndex]);

  // Scroll Interface History table back into view when page changes
  useEffect(() => {
    if (nicPageIndex > 0 && nicTableRef.current) {
      nicTableRef.current.scrollIntoView({ behavior: 'auto', block: 'start' });
    }
  }, [nicPageIndex]);

  const userPaginationProps = {
    itemCount: userTotalItems,
    pageCount: userPageCount,
    data: userHistoryItems,
    isPaginable: userTotalItems > 0,
    isLoading: getUserHistoryLoading,
  };

  return (
    <VStack w="100%" alignItems="start" spacing={3} p={'8px'}>
      {/* User History Table */}
      {getUserHistoryLoading && userTotalItems === 0 ? (
        <VStack ref={userTableRef} w="100%" alignItems="start" spacing={3}>
          <Panel w="100%">
            <Center py={8}>
              <LoadingSpinner />
            </Center>
          </Panel>
        </VStack>
      ) : userHistoryItems.length === 0 ? (
        <NoDataSection header={null} />
      ) : (
        <Panel>
          <FlushPanelContent>
            <DataTable
              isLined
              data-testid="crowdstrike-user-history-table"
              pageSize={userPageSize}
              autoResetPage={false}
              columns={dataTableUserColumns}
              autoResetSortBy={false}
              isPaginationManual={true}
              onFetchData={userOnFetchData}
              initialState={{
                pageIndex: userPageIndex,
                pageSize: userPageSize,
              }}
              {...userPaginationProps}
              emptyBodyContent={
                <Center py={8}>
                  <Text color="gray.400">No user history data available</Text>
                </Center>
              }
            />
          </FlushPanelContent>
        </Panel>
      )}
    </VStack>
  );
};
